import React, { useState, useEffect, useRef } from "react";
import "./PreventiveReportView.css";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import API_URL from "../Config";
import { IoMdArrowBack } from "react-icons/io";
import Pagination from "../Component/Pagination";

function AmcReportView({ isSidebarExpanded }) {
  // FIXED ORDER: must match route
  const { department_id, filter_option, status, startDate, endDate } = useParams();

  const [reports, setReports] = useState([]);
  const [departmentName, setDepartmentName] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [isAscending, setIsAscending] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  const containerRef = useRef(null);

  // ----------------------------------------------------------
  // FETCH DATA WITH CONDITIONAL DATES
  // ----------------------------------------------------------
  const fetchData = async (currentPage, rowsPerPage) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = {
        department_id,
        filter_option,
        status,
        page: currentPage,
        per_page: rowsPerPage,
        ...(startDate && endDate && {
          start_date: startDate,
          end_date: endDate,
        }),
      };

      const response = await axios.get(`${API_URL}/api/get_amc_report_schedule_details`, {
        headers: { Authorization: authKey },
        params,
      });

      setReports(response.data.schedules);
      setTotalItems(response.data.total);
      setDepartmentName(response.data.department_name);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching amc report:", error.message);
      setLoading(false);
    }
  };

  // ----------------------------------------------------------
  // INITIAL FETCH + REFETCH when params change
  // ----------------------------------------------------------
  useEffect(() => {
    const storedPage = localStorage.getItem("currentPage");
    const storedPerPage = localStorage.getItem("itemsPerPage");

    const currentPage = storedPage ? parseInt(storedPage, 10) : 1;
    const perPage = storedPerPage ? parseInt(storedPerPage, 10) : 50;

    setPage(currentPage);
    setItemsPerPage(perPage);

    fetchData(currentPage, perPage);
  }, [department_id, filter_option, status, startDate, endDate]);

  const handlePaginationChange = (currentPage, rowsPerPage) => {
    setPage(currentPage);
    setItemsPerPage(rowsPerPage);
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("itemsPerPage", rowsPerPage);
  };

  const handleRefresh = () => {
    fetchData(page, itemsPerPage);
  };

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSortpopup = () => {
    setIsSortPopup(!isSortPopup);
  };

  const closePopups = () => {
    setIsSortPopup(false);
  };

  const handleBackScheduler = () => {
    navigate("/general-report?tab=amc-reports");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const formatter = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
    return formatter.format(date);
  };

  const handleViewClick = (scheduler_id) => {
    navigate(`/amc-scheduler-view/${scheduler_id}`);
  };

  const filterOptionMap = {
    all: "All",
    today: "Today",
    yesterday: "Yesterday",
    tomorrow: "Tomorrow",
    next7days: "Next 7 Days",
    next_month: "Next Month",
    current_week: "Current Week",
    previous_month: "Previous Month",
    past_week: "Previous Week",
    current_month: "Current Month",
    custom_date: "Custom Date",
  };

  const displayText = filterOptionMap[filter_option] || filter_option;

  const formatDates = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(date);
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content" ref={containerRef}>
          <div className="data-list-header">
            <div className="data-list-header-content">
              <div className="data-list-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack className="back-company" onClick={handleBackScheduler} />
                </Tooltip>

                {reports.length > 0 && (
                  <>
                    <span className="report-name">AMC Report</span>

                    <span
                      className="status-show"
                      style={{
                        color: "green",
                        fontWeight: "500",
                      }}
                    >
                      {reports[0].status
                        ? reports[0].status.charAt(0).toUpperCase() +
                          reports[0].status.slice(1).toLowerCase()
                        : "NA"}
                    </span>

                    <span
                      className="department-show"
                      style={{ color: "green", marginLeft: "15px", fontWeight: "500" }}
                    >
                      {departmentName
                        ? departmentName.charAt(0).toUpperCase() + departmentName.slice(1).toLowerCase()
                        : "NA"}
                    </span>

                    {/* Conditional date display */}
                    <span className="option-show" style={{ color: "#ce9139ed", marginLeft: "15px" }}>
                      {startDate && endDate
                        ? `${formatDates(startDate)} - ${formatDates(endDate)}`
                        : displayText}
                    </span>
                  </>
                )}
              </div>

              <div className="data-list-heading-content-right-side">
                <div className="search-bar-container">
                  <input
                    type="text"
                    placeholder="Search ..."
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    className="search-bar"
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>

                <Tooltip title="Refresh List" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>

                <button className="sort-button" onClick={handleSortpopup}>
                  <PiDotsThreeOutlineVerticalFill className="sort-icon" />
                </button>

                {isSortPopup && (
                  <div className="sort-popup-container">
                    <div className="sort-popup-content">
                      <div className="sort-by">
                        SORT BY {isAscending ? "(Ascending)" : "(Descending)"}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="header-separator"></div>

          <div className="data-list-body" onClick={closePopups}>
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : reports.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>DEPARTMENT</th>
                        <th>SERVICE PROVIDER NAME</th>
                        <th>SERVICE DATE</th>
                        <th>AMC NAME</th>
                        <th className="center-alignment">STATUS</th>
                        <th>GENERATED AT</th>
                        <th className="center-alignment">ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {reports.map((report, index) => (
                        <tr key={index} className="hoverable-row">
                          <td>{index + 1}</td>
                          <td>{departmentName || "NA"}</td>
                          <td>{report.service_provider_name || "NA"}</td>
                          <td>{formatDate(report.service_date || "NA")}</td>
                          <td>{report.amc_name || "NA"}</td>

                          <td className="status-schedules center-alignment">
                            <div
                              style={{
                                backgroundColor:
                                  report.status === "Hold"
                                    ? "#D4AC0D"
                                    : report.status === "Cancelled"
                                    ? "#F81700"
                                    : report.status === "not in tenure"
                                    ? "#313639"
                                    : report.status === "overdue"
                                    ? "#C0612B"
                                    : report.status === "active"
                                    ? "#2980B9"
                                    : report.status === "In Progress"
                                    ? "#df7f26"
                                    : report.status === "Completed"
                                    ? "#2E8B57"
                                    : "transparent",
                                width: "6vw",
                              }}
                            >
                              {report.status
                                ? report.status.charAt(0).toUpperCase() +
                                  report.status.slice(1).toLowerCase()
                                : "NA"}
                            </div>
                          </td>

                          <td>{formatDate(report.created_at || "NA")}</td>

                          <td className="center-alignment">
                            <button
                              onClick={() => handleViewClick(report.id)}
                              className="button-bg"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No Amc Report Found</div>
                  </div>
                )}
              </div>

              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AmcReportView;
















// import React, { useState, useEffect, useRef } from "react";
// import "./PreventiveReportView.css";
// import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";
// import axios from "axios";
// import { useParams, useNavigate } from "react-router-dom";
// import { IoSearchOutline } from "react-icons/io5";
// import { TbRefresh } from "react-icons/tb";
// import Tooltip from "@mui/material/Tooltip";
// import API_URL from "../Config";
// import { IoMdArrowBack } from "react-icons/io";
// import Pagination from "../Component/Pagination";

// function AmcReportView({ isSidebarExpanded }) {
//   const { department_id, status, filter_option, startDate, endDate } =
//     useParams();
//   const [reports, setReports] = useState([]);
//   const [departmentName, setDepartmentName] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [isSortPopup, setIsSortPopup] = useState(false);
//   const [isAscending, setIsAscending] = useState(true);
//   const [searchInput, setSearchInput] = useState("");
//   const navigate = useNavigate();
//   const [page, setPage] = useState(1);
//   const [itemsPerPage, setItemsPerPage] = useState(50);
//   const [totalItems, setTotalItems] = useState(0);
//   const containerRef = useRef(null);

//   const fetchData = async (currentPage, itemsPerPage) => {
//     try {
//       setLoading(true);
//       const authKey = localStorage.getItem("authKey");

//       const response = await axios.get(
//         `${API_URL}/api/get_amc_report_schedule_details`,
//         {
//           headers: {
//             Authorization: authKey,
//           },
//           params: {
//             department_id,
//             filter_option: filter_option,
//             status,
//             page: currentPage,
//             per_page: itemsPerPage,
//             start_date: startDate,
//             end_date: endDate,
//           },
//         }
//       );

//       // Adjusted to handle the correct property names
//       setReports(response.data.schedules);
//       setTotalItems(response.data.total);
//       setDepartmentName(response.data.department_name);
//       setLoading(false);
//     } catch (error) {
//       console.error("Error fetching amc report:", error.message);
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     const storedPage = localStorage.getItem("currentPage");
//     const storedItemsPerPage = localStorage.getItem("itemsPerPage");

//     const page = storedPage ? parseInt(storedPage, 10) : 0;
//     const itemsPerPage = storedItemsPerPage
//       ? parseInt(storedItemsPerPage, 10)
//       : 50;
//     setPage(page);
//     setItemsPerPage(itemsPerPage);

//     fetchData(page, itemsPerPage);
//   }, [department_id, filter_option, status, page, itemsPerPage, startDate, endDate]);

//   const handlePaginationChange = (currentPage, itemsPerPage) => {
//     setPage(currentPage);
//     setItemsPerPage(itemsPerPage);

//     // Store values in local storage
//     localStorage.setItem("currentPage", currentPage);
//     localStorage.setItem("itemsPerPage", itemsPerPage);
//   };

//   const handleSearchInputChange = (event) => {
//     setSearchInput(event.target.value);
//   };

//   const handleSortpopup = () => {
//     setIsSortPopup(!isSortPopup);
//   };

//   const closePopups = () => {
//     setIsSortPopup(false);
//   };

//   const handleRefresh = () => {
//     fetchData();
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formatter = new Intl.DateTimeFormat("en-GB", {
//       day: "2-digit",
//       month: "2-digit",
//       year: "2-digit",
//     });
//     return formatter.format(date);
//   };
//   const handleBackScheduler = () => {
//     navigate("/general-report?tab=amc-reports");
//   };

//   const getStatusColor = (status) => {
//     switch (status.toLowerCase()) {
//       case "active":
//         return "#2980B9";
//       case "cancelled":
//         return "#F81700";
//       case "hold":
//         return "#D4AC0D";
//       case "not in tenure":
//         return "#313639";
//       case "overdue":
//         return "#C0612B";
//       case "In Progress":
//         return "#df7f26";
//       case "completed":
//         return "#2E8B57";
//       default:
//         return "black"; // Fallback color if needed
//     }
//   };
//   const handleViewClick = async (scheduler_id) => {
//     navigate(`/amc-scheduler-view/${scheduler_id}`);
//   };
//   const filterOptionMap = {
//     all: "All",
//     today: "Today",
//     yesterday: "Yesterday",
//     tomorrow: "Tomorrow",
//     next7days: "Next 7 Days",
//     next_month: "Next Month",
//     current_week: "Current Week",
//     previous_month: "Previous Month",
//     past_week: "Previous Week",
//     current_month: "Current Month",
//     custom_date: "Custom Date",
//   };
//   const displayText = filterOptionMap[filter_option] || filter_option;

//   const formatDates = (dateString) => {
//     if (!dateString || typeof dateString !== "string") {
//       return "";
//     }
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) {
//       return "";
//     }

//     const formatter = new Intl.DateTimeFormat("en-GB", {
//       day: "2-digit",
//       month: "2-digit",
//       year: "2-digit",
//     });
//     return formatter.format(date);
//   };

//   return (
//     <>
//       <div
//         className={`data-list-content-container page-content ${
//           isSidebarExpanded ? "expanded" : "collapsed"
//         }`}
//       >
//         <div className="data-list-content" ref={containerRef}>
//           <div className="data-list-header">
//             <div className="data-list-header-content">
//               <div className="data-list-heading-content-left-side">
//                 <Tooltip title="Back" arrow>
//                   <IoMdArrowBack
//                     className="back-company"
//                     onClick={handleBackScheduler}
//                   />
//                 </Tooltip>
//                 {reports.length > 0 && (
//                   <>
//                     <span className="report-name">AMC Report</span>
//                     <span
//                       className="status-show"
//                       style={{
//                         color: getStatusColor(reports[0].status),
//                         fontWeight: "500",
//                       }}
//                     >
//                       {reports[0].status
//                         ? reports[0].status.charAt(0).toUpperCase() +
//                           reports[0].status.slice(1).toLowerCase()
//                         : "NA"}
//                     </span>
//                     <span
//                       className="department-show"
//                       style={{
//                         color: "green",
//                         marginLeft: "15px",
//                         fontWeight: "500",
//                       }}
//                     >
//                       {departmentName
//                         ? departmentName.charAt(0).toUpperCase() +
//                           departmentName.slice(1).toLowerCase()
//                         : "NA"}
//                     </span>
//                     <span
//                       className="option-show"
//                       style={{
//                         color: "#ce9139ed",
//                         marginLeft: "15px",
//                         fontWeight: "500",
//                       }}
//                     >
//                       {/* {displayText} */}
//                       {formatDates(startDate)} - {formatDates(endDate)}
//                     </span>
//                   </>
//                 )}
//               </div>

//               <div className="data-list-heading-content-right-side">
//                 <div className="search-bar-container">
//                   <input
//                     type="text"
//                     placeholder="Search ..."
//                     value={searchInput}
//                     onChange={handleSearchInputChange}
//                     className="search-bar"
//                   />
//                   <IoSearchOutline className="search-bar-icon" />
//                 </div>

//                 <Tooltip title="Refresh List" arrow>
//                   <button className="refresh-button" onClick={handleRefresh}>
//                     <TbRefresh className="refresh-icon" />
//                   </button>
//                 </Tooltip>
//                 <button className="sort-button" onClick={handleSortpopup}>
//                   <PiDotsThreeOutlineVerticalFill className="sort-icon" />
//                 </button>
//                 {isSortPopup && (
//                   <div className="sort-popup-container">
//                     <div className="sort-popup-content">
//                       <div className="sort-by">
//                         SORT BY {isAscending ? "(Ascending)" : "(Descending)"}
//                       </div>
//                       <div className="sort-by-button"></div>
//                     </div>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//           <div class="header-separator"></div>
//           <div className="data-list-body" onClick={closePopups}>
//             <div className="data-list-scroll-body">
//               <div className="data-list-table">
//                 {loading ? (
//                   <div className="loader-container">
//                     <div className="loader"></div>
//                     <div>Loading...</div>
//                   </div>
//                 ) : reports.length > 0 ? (
//                   <table className="table multilevel-data">
//                     <thead>
//                       <tr>
//                         <th>SR. NO.</th>
//                         <th>DEPARTMENT</th>
//                         <th>SERVICE PROVIDER NAME</th>
//                         <th>SERVICE DATE</th>
//                         <th>AMC NAME</th>
//                         <th className="center-alignment">STATUS</th>
//                         <th>GENERATED AT</th>
//                         <th className="center-alignment">ACTION</th>
//                       </tr>
//                     </thead>
//                     <tbody className="data-list-scroll-bodys">
//                       {reports.map((report, index) => (
//                         <tr key={index} className="hoverable-row">
//                           <td>{index + 1}</td>
//                           <td>{departmentName || "NA"}</td>
//                           <td>{report.service_provider_name || "NA"}</td>
//                           <td>{formatDate(report.service_date || "NA")}</td>
//                           <td>{report.amc_name || "NA"}</td>
//                           <td className="status-schedules center-alignment">
//                             <div
//                               style={{
//                                 backgroundColor:
//                                   report.status === "Hold"
//                                     ? "#D4AC0D"
//                                     : report.status === "Cancelled"
//                                     ? "#F81700"
//                                     : report.status === "not in tenure"
//                                     ? "#313639"
//                                     : report.status === "overdue"
//                                     ? "#C0612B"
//                                     : report.status === "active"
//                                     ? "#2980B9"
//                                     : report.status === "In Progress"
//                                     ? "#df7f26"
//                                     : report.status === "Completed"
//                                     ? "#2E8B57"
//                                     : "transparent",
//                                 width: "6vw",
//                               }}
//                             >
//                               {/* {report.status || "NA"} */}
//                               {report.status
//                                 ? report.status.charAt(0).toUpperCase() +
//                                   report.status.slice(1).toLowerCase()
//                                 : "NA"}
//                             </div>
//                           </td>
//                           <td>{formatDate(report.created_at || "NA")}</td>
//                           <td className="center-alignment">
//                             <button
//                               onClick={() => handleViewClick(report.id)}
//                               className="button-bg"
//                             >
//                               View
//                             </button>
//                           </td>
//                         </tr>
//                       ))}
//                     </tbody>
//                   </table>
//                 ) : (
//                   <div className="no-users-container">
//                     <div className="no-users-message">No Amc Report Found</div>
//                   </div>
//                 )}
//               </div>
//               <Pagination
//                 showPerPage={itemsPerPage}
//                 total={totalItems}
//                 onPaginationChange={handlePaginationChange}
//               />
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// export default AmcReportView;
